import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Clock, Shield, Zap, Target, AlertTriangle } from 'lucide-react';

const WHY_NOW_FACTORS = [
  {
    title: "The Great Insurance Disruption",
    subtitle: "Your Competition is Already Moving",
    description: "While you're reading this, InsurTech startups are processing workflows in minutes, not months. Traditional carriers who don't adapt now will become footnotes in insurance history.",
    icon: TrendingUp,
    color: "from-red-500 to-orange-500",
    urgency: "HIGH"
  },
  {
    title: "Customer Expectations Have Changed Forever",
    subtitle: "The Amazon Effect Hits Insurance",
    description: "Your customers order everything with one click and expect the same from insurance. Every day you delay is another day they consider switching to someone faster.",
    icon: Clock,
    color: "from-blue-500 to-cyan-500",
    urgency: "CRITICAL"
  },
  {
    title: "Regulatory Tsunami Coming",
    subtitle: "New Rules, Old Systems Don't Mix",
    description: "AI governance, data privacy, and digital-first regulations are reshaping the industry. Companies with legacy systems will drown in compliance costs.",
    icon: Shield,
    color: "from-purple-500 to-pink-500",
    urgency: "IMMINENT"
  },
  {
    title: "The Talent Crisis Deepens",
    subtitle: "Your Best People Are Burning Out",
    description: "Experienced underwriters and back-office specialists are retiring faster than you can replace them. AI genes don't retire, don't burn out, and work 24/7.",
    icon: AlertTriangle,
    color: "from-yellow-500 to-red-500",
    urgency: "URGENT"
  }
];

const MARKET_STATS = [
  {
    stat: "73%",
    label: "of insurance executives say AI is critical for future success",
    source: "McKinsey Global Insurance Report 2024"
  },
  {
    stat: "45%",
    label: "reduction in data processing time with AI implementation",
    source: "Insurance Innovation Study 2024"
  },
  {
    stat: "$1.3T",
    label: "potential value creation from AI in insurance by 2030",
    source: "PwC AI Analysis Report"
  },
  {
    stat: "89%",
    label: "of customers would switch for better digital experience",
    source: "Digital Insurance Consumer Survey 2024"
  }
];

const WhyNowSection: React.FC = () => {
  return (
    <section id="why-now" className="py-20 bg-gradient-to-b from-black to-red-900/20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400">
              Why Now?
            </span>
          </h2>
          <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            The insurance industry is at an inflection point. The companies that act now will dominate the next decade. 
            Those that wait will become cautionary tales.
          </p>
        </motion.div>

        {/* Market Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {MARKET_STATS.map((stat, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 text-center">
              <div className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400 mb-2">
                {stat.stat}
              </div>
              <div className="text-gray-300 mb-2 font-medium">
                {stat.label}
              </div>
              <div className="text-xs text-gray-500">
                {stat.source}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Why Now Factors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {WHY_NOW_FACTORS.map((factor, index) => {
            const IconComponent = factor.icon;
            return (
              <motion.div
                key={factor.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative group"
              >
                <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8 h-full hover:border-gray-600/50 transition-all duration-300">
                  {/* Urgency Badge */}
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold mb-4 bg-gradient-to-r ${factor.color} text-white`}>
                    {factor.urgency}
                  </div>

                  {/* Icon and Header */}
                  <div className="flex items-start mb-4">
                    <div className={`p-3 rounded-lg bg-gradient-to-r ${factor.color} mr-4 flex-shrink-0`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">{factor.title}</h3>
                      <p className={`text-lg font-medium bg-gradient-to-r ${factor.color} bg-clip-text text-transparent`}>
                        {factor.subtitle}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 text-lg leading-relaxed">
                    {factor.description}
                  </p>

                  {/* Hover Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${factor.color} opacity-0 group-hover:opacity-5 rounded-xl transition-opacity duration-300`}></div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* The Choice Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-gray-900/50 to-red-900/30 rounded-2xl p-12 border border-red-500/20">
            <h3 className="text-4xl font-bold text-white mb-6">
              You Have Two Choices
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Choice 1 - Wait */}
              <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6">
                <h4 className="text-2xl font-bold text-red-400 mb-4">Wait and See</h4>
                <ul className="text-gray-300 space-y-2 text-left">
                  <li>• Watch competitors gain market share</li>
                  <li>• Lose customers to faster, smarter alternatives</li>
                  <li>• Struggle with increasing compliance costs</li>
                  <li>• Fight the talent shortage with outdated tools</li>
                  <li>• Become a cautionary tale in industry reports</li>
                </ul>
              </div>

              {/* Choice 2 - Act Now */}
              <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-xl p-6">
                <h4 className="text-2xl font-bold text-emerald-400 mb-4">Act Now</h4>
                <ul className="text-gray-300 space-y-2 text-left">
                  <li>• Lead your market with AI-powered operations</li>
                  <li>• Delight customers with instant, intelligent service</li>
                  <li>• Turn compliance from cost center to competitive advantage</li>
                  <li>• Empower your team with superhuman capabilities</li>
                  <li>• Become the case study others wish they could replicate</li>
                </ul>
              </div>
            </div>

            <p className="text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
              The window for first-mover advantage is closing. Every day you wait is market share you'll never get back.
            </p>

            <motion.a
              href="#contact"
              className="inline-flex items-center gap-2 px-12 py-6 rounded-full text-xl font-bold bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white transition-all duration-300 shadow-[0_0_30px_rgba(239,68,68,0.3)] hover:shadow-[0_0_40px_rgba(239,68,68,0.4)]"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Zap className="w-6 h-6" />
              Don't Wait - Start Your AI Evolution Today
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyNowSection;
